import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CommentState = {
  dataListComment?: CommentsType;
  dataLatestOrderComment?: CommentsContentType;
  fileViewUrls: Record<string, ViewUrlsBulkItemType | undefined>;
};

export const commentsInitialState: CommentState = {
  fileViewUrls: {},
};

const sortCommentsByCreatedAtAsc = (comments: CommentsContentType[] = []) => {
  return [...comments].sort(
    (left, right) =>
      new Date(left.createdAtUtc ?? 0).getTime() - new Date(right.createdAtUtc ?? 0).getTime(),
  );
};

const slice = createSlice({
  name: 'comments',
  initialState: commentsInitialState,
  reducers: {
    setListCommentAction(state, { payload }: PayloadAction<{ dataListComment: CommentsType }>) {
      const sortedContent = sortCommentsByCreatedAtAsc(payload.dataListComment.content ?? []);

      state.dataListComment = {
        ...payload.dataListComment,
        content: sortedContent,
      };
      state.dataLatestOrderComment = sortedContent[sortedContent.length - 1];
    },
    upsertRealtimeOrderCommentAction(
      state,
      { payload }: PayloadAction<{ comment: CommentsContentType }>,
    ) {
      const currentContent = [...(state.dataListComment?.content ?? [])];
      const existedIndex = currentContent.findIndex((item) => item.id === payload.comment.id);
      const nextComment = payload.comment;
      const previousComment = existedIndex >= 0 ? currentContent[existedIndex] : null;

      if (existedIndex >= 0) {
        currentContent[existedIndex] = {
          ...currentContent[existedIndex],
          ...nextComment,
        };
      } else {
        currentContent.push(nextComment);
      }

      const sortedContent = sortCommentsByCreatedAtAsc(currentContent);

      const baseUnreadCount = state.dataListComment?.unreadCount ?? 0;
      const baseUnreadByOther = state.dataListComment?.unreadByOther ?? 0;

      state.dataListComment = {
        unreadCount:
          existedIndex >= 0
            ? baseUnreadCount
            : baseUnreadCount + (nextComment.isMine ? 0 : 1),
        unreadByOther:
          existedIndex >= 0
            ? baseUnreadByOther
            : baseUnreadByOther + (nextComment.isMine ? 1 : 0),
        code: state.dataListComment?.code ?? 200,
        messages: state.dataListComment?.messages ?? [],
        succeeded: state.dataListComment?.succeeded ?? true,
        content: sortedContent,
      };

      state.dataLatestOrderComment =
        previousComment && previousComment.createdAtUtc === nextComment.createdAtUtc
          ? nextComment
          : sortedContent[sortedContent.length - 1];
    },
    applyRealtimeOrderCommentsReadAction(
      state,
      {
        payload,
      }: PayloadAction<{ event: OrderCommentsReadRealtimePayload; currentUserId: string }>,
    ) {
      if (!state.dataListComment?.content?.length) return;

      const isCurrentUserReader = payload.event.readerUserId === payload.currentUserId;

      state.dataListComment.content = state.dataListComment.content.map((comment) => {
        if (comment.orderId !== payload.event.orderId) return comment;

        if (isCurrentUserReader && !comment.isMine) {
          return {
            ...comment,
            isRead: true,
            isReadByOrder: true,
          };
        }

        if (!isCurrentUserReader && comment.author?.id === payload.currentUserId) {
          return {
            ...comment,
            isReadByOther: true,
            seenByRecipient: true,
          };
        }

        return comment;
      });

      state.dataListComment.unreadCount = payload.event.unreadCount;
      state.dataListComment.unreadByOther = payload.event.unreadByOther;
      state.dataLatestOrderComment =
        state.dataListComment.content[state.dataListComment.content.length - 1];
    },
    markIncomingOrderCommentsReadAction(
      state,
      { payload }: PayloadAction<{ orderId: string; upToCommentId: string }>,
    ) {
      if (!state.dataListComment?.content?.length) return;

      const targetComment = state.dataListComment.content.find(
        (comment) => comment.id === payload.upToCommentId && comment.orderId === payload.orderId,
      );

      if (!targetComment) return;

      const targetCreatedAt = new Date(targetComment.createdAtUtc ?? 0).getTime();

      state.dataListComment.content = state.dataListComment.content.map((comment) => {
        const commentCreatedAt = new Date(comment.createdAtUtc ?? 0).getTime();

        if (
          comment.orderId === payload.orderId &&
          !comment.isMine &&
          commentCreatedAt <= targetCreatedAt
        ) {
          return {
            ...comment,
            isRead: true,
            isReadByOrder: true,
          };
        }

        return comment;
      });

      state.dataListComment.unreadCount = state.dataListComment.content.filter(
        (comment) => comment.orderId === payload.orderId && !comment.isMine && !comment.isRead,
      ).length;

      state.dataLatestOrderComment =
        state.dataListComment.content[state.dataListComment.content.length - 1];
    },
    setFileViewUrlsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        fileViewUrls: Record<string, ViewUrlsBulkItemType | undefined>;
      }>,
    ) {
      state.fileViewUrls = {
        ...state.fileViewUrls,
        ...payload.fileViewUrls,
      };
    },
    clearFileViewUrlsAction(state, { payload }: PayloadAction<string[] | undefined>) {
      if (!payload?.length) {
        state.fileViewUrls = {};
        return;
      }

      for (const fileId of payload) {
        delete state.fileViewUrls[fileId];
      }
    },
  },
});

export const {
  setListCommentAction,
  upsertRealtimeOrderCommentAction,
  applyRealtimeOrderCommentsReadAction,
  markIncomingOrderCommentsReadAction,
  setFileViewUrlsAction,
  clearFileViewUrlsAction,
} = slice.actions;
export default slice.reducer;
